import React from 'react';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  alpha,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { FileText, RotateCcw } from 'lucide-react';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { CreateProject } from '../CreateProject';
import { GetProjectResponse } from '../../api/protosCompiled/project/project_pb';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { useFetchUsers } from '../../hooks/useFetchUsers';

interface ProjectListContentProps {
  projects: GetProjectResponse.AsObject[];
  error: Error | null;
  isLoading: boolean;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onEditProject: (project: GetProjectResponse.AsObject) => void;
  onLoadProject: (project: GetProjectResponse.AsObject) => void;
  isCreatingProject: boolean;
  isUpdatingProject: boolean;
  selectedProjectId: string;
  onCloseDialog: () => void;
  selectedHierarchyId: string | null;
  selectedUserId: string | null;
  onUserFilterChange: (userId: string | null) => void;
  showContractsOnly: boolean;
  onContractFilterChange: (showContractsOnly: boolean) => void;
  showHierarchyAssignedOnly: boolean;
  onHierarchyFilterChange: (showHierarchyAssignedOnly: boolean) => void;
  onResetFilters: () => void;
  createProjectHierarchyId?: string | null;
}

export const ProjectListContent: React.FC<ProjectListContentProps> = ({
  projects,
  error,
  isLoading,
  searchTerm,
  onSearchChange,
  onEditProject,
  onLoadProject,
  isCreatingProject,
  isUpdatingProject,
  selectedProjectId,
  onCloseDialog,
  selectedHierarchyId,
  selectedUserId,
  onUserFilterChange,
  showContractsOnly,
  onContractFilterChange,
  showHierarchyAssignedOnly,
  onHierarchyFilterChange,
  onResetFilters,
  createProjectHierarchyId,
}) => {
  const theme = useTheme();

  const setShowSidebarMenu = useStore(useUnityBuildStore).setShowSidebarMenu;
  const { data: users } = useFetchUsers();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
    setShowSidebarMenu(false);
  };

  const handleEditProject = (e: React.MouseEvent, project: GetProjectResponse.AsObject) => {
    e.stopPropagation();
    onEditProject(project);
  };

  const errorMessage = (error as Error)?.message;

  return (
    <>
      {/* Search and Create Section */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={4}>
            <TextField
              placeholder="Search Projects..."
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              variant="outlined"
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon aria-hidden="true" sx={{ mr: 1 }} />,
              }}
              id="project-search"
              aria-label="Search projects"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.23)'
                        : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2} md={2}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.23)'
                        : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            >
              <InputLabel id="user-filter-label">Filter by Creator</InputLabel>
              <Select
                labelId="user-filter-label"
                value={selectedUserId || ''}
                onChange={e => onUserFilterChange(e.target.value || null)}
                label="Filter by Creator"
                startAdornment={
                  <FilterListIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
                }
                displayEmpty
                renderValue={value => {
                  if (!value) return 'All Creators';
                  const user = users?.find(u => u.user?.id === value);
                  return user?.user?.name || 'Unknown User';
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">All Creators</Typography>
                    <Chip
                      size="small"
                      label={projects.length}
                      sx={{
                        height: 20,
                        fontSize: '0.75rem',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                </MenuItem>
                {users
                  ?.filter(user => {
                    const userProjectCount = projects.filter(p => {
                      const matches = p.project?.createdbyuserid === user.user?.id;
                      return matches;
                    }).length;
                    return userProjectCount > 0;
                  })
                  .map(user => {
                    const userProjectCount = projects.filter(p => {
                      const matches = p.project?.createdbyuserid === user.user?.id;
                      return matches;
                    }).length;
                    return (
                      <MenuItem key={user.user?.id} value={user.user?.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {user.user?.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={userProjectCount}
                            sx={{
                              height: 20,
                              fontSize: '0.75rem',
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }}
                          />
                        </Box>
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2} md={2}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.23)'
                        : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            >
              <InputLabel id="contract-filter-label">Contract Status</InputLabel>
              <Select
                labelId="contract-filter-label"
                value={showContractsOnly ? 'contracts' : 'all'}
                onChange={e => onContractFilterChange(e.target.value === 'contracts')}
                label="Contract Status"
                startAdornment={
                  <FilterListIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
                }
              >
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">All Projects</Typography>
                    <Chip
                      size="small"
                      label={projects.length}
                      sx={{
                        height: 20,
                        fontSize: '0.75rem',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                </MenuItem>
                <MenuItem value="contracts">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">With Contracts</Typography>
                    <Chip
                      size="small"
                      label={
                        projects.filter(
                          p =>
                            p.project?.projectcontractsList &&
                            p.project.projectcontractsList.length > 0
                        ).length
                      }
                      sx={{
                        height: 20,
                        fontSize: '0.75rem',
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                      }}
                    />
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Reset Filters Button */}
          <Grid item xs={12} sm={2} md={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RotateCcw size={16} />}
              onClick={onResetFilters}
              sx={{
                height: 40,
                width: '100%',
                fontSize: '0.875rem',
                textTransform: 'none',
                borderColor: theme.palette.text.secondary,
                color: theme.palette.text.secondary,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              my: 4,
            }}
            role="status"
            aria-label="Loading projects"
          >
            <CircularProgress aria-label="Loading" />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            sx={{
              flexGrow: 1,
              mb: 2,
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              '& .MuiAlert-icon': {
                color: theme.palette.error.main,
              },
            }}
            role="alert"
          >
            Error: {errorMessage}
          </Alert>
        ) : projects && projects.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={{
              flexGrow: 1,
              height: '100%',
              backgroundColor: theme.palette.background.paper,
              boxShadow: 'none',
              borderRadius: 0,
              overflow: 'auto',
            }}
          >
            <Table
              stickyHeader
              aria-label="Projects table"
              role="grid"
              size="medium"
              sx={{
                tableLayout: 'fixed',
                width: '100%',
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      width: '20%',
                    }}
                  >
                    Project Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      width: '25%',
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      width: '25%',
                    }}
                  >
                    Address
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      width: '12%',
                    }}
                  >
                    Construction Start Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      width: '12%',
                    }}
                  >
                    Construction End Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      width: '6%',
                      textAlign: 'center',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects
                  .filter(project => {
                    // Apply contract filter if enabled
                    if (showContractsOnly) {
                      return (
                        project.project?.projectcontractsList &&
                        project.project.projectcontractsList.length > 0
                      );
                    }

                    // Apply hierarchy assignment filter if enabled
                    if (showHierarchyAssignedOnly) {
                      return (
                        !project.project?.projecthierarchyid ||
                        project.project.projecthierarchyid.trim() === ''
                      );
                    }

                    return true;
                  })
                  .map(project => (
                    <TableRow
                      key={project.project!.id}
                      onClick={() => onLoadProject(project)}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === 'dark' ? 0.1 : 0.05
                          ),
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.common.white, 0.02)
                              : alpha(theme.palette.common.black, 0.02),
                        },
                      }}
                      role="row"
                      aria-label={`Project: ${project.project!.name}`}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                              flex: 1,
                            }}
                          >
                            {project.project!.name}
                          </Typography>
                          {project.project?.projectcontractsList &&
                            project.project.projectcontractsList.length > 0 && (
                              <FileText
                                size={16}
                                color={theme.palette.success.main}
                                style={{ flexShrink: 0 }}
                              />
                            )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {project.project!.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {project.project!.address}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDateForDisplay(project.project!.startdate)}</TableCell>
                      <TableCell>{formatDateForDisplay(project.project!.enddate)}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip
                          title={`Edit project details for ${project.project!.name}`}
                          arrow
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            onClick={e => handleEditProject(e, project)}
                            color="primary"
                            aria-label={`Edit project ${project.project!.name}`}
                          >
                            <EditIcon fontSize="small" aria-hidden="true" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              p: 4,
              textAlign: 'center',
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            role="status"
            aria-live="polite"
          >
            <Typography variant="body1" color="text.secondary">
              {searchTerm ? 'No projects match your search criteria.' : 'No projects available.'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Dialogs */}
      <Dialog
        open={isCreatingProject || isUpdatingProject}
        onClose={onCloseDialog}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            maxWidth: '600px',
            width: '100%',
            m: 2,
          },
        }}
        aria-labelledby="project-dialog-title"
      >
        <DialogContent>
          <CreateProject
            create={isCreatingProject}
            update={isUpdatingProject}
            updateProjectId={selectedProjectId}
            selectedHierarchyId={createProjectHierarchyId}
            setIsCreatingProject={() => {}}
            setIsUpdatingProject={() => {}}
            setIsCreatingLoading={() => {}}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
